import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { ConfigService } from '@mona/config';
import { BaseErrors, Suspense, SUSPENSE_INITIAL_STATE } from '@mona/shared/utils';
import { WsEndpointsList, WS_ENDPOINTS_LIST } from '../tokens';
import { WsService } from './base-ws.service';

/**
 * Core health ws service
 */
@Injectable({ providedIn: 'root' })
export class ApiHealthWsService {
    reconnectInterval = 3000;
    /** Abort CONNECTING sockets that never open (router blackhole / TCP hang). */
    connectTimeoutMs = 5000;
    reconnectAttempts = 0;

    private wsMap = new Map<string, WsService<any>>();
    private apiStateMap = new Map<string, BehaviorSubject<Suspense<void>>>([]);
    private activeCheckSub: Subscription | null = null;
    private connectTimer: ReturnType<typeof setTimeout> | null = null;

    /**
     * Constructor
     * @param configService ConfigService
     * @param wsEnpoints
     */
    constructor(
        private configService: ConfigService,
        @Inject(WS_ENDPOINTS_LIST) public readonly wsEnpoints: WsEndpointsList[],
    ) {
        this.configService.configLoaded$.subscribe(() => {
            this.wsEnpoints.forEach(wsApi => {
                const serverUrl = this.configService.get(wsApi) as string;
                this.setUrlMapsValues(serverUrl);
            });
        });
    }

    /**
     * Get state
     * @param serverUrl
     */
    getState$(serverUrl: string): Observable<Suspense<void>> {
        const state = this.apiStateMap.get(serverUrl);
        return state?.asObservable() || of({ finished: true, inProgress: false, succeeded: true } as any);
    }

    /**
     * Checks connection. Unsubscribing aborts without reporting failure (Case A mid-probe online).
     * @param serverUrl
     * @param retryAttemps
     */
    checkConnection(serverUrl: string, retryAttemps = 0): Observable<Suspense<any>> {
        let checkingState = this.apiStateMap.get(serverUrl);

        if (!checkingState) {
            this.setUrlMapsValues(serverUrl);
            checkingState = this.apiStateMap.get(serverUrl);
        }

        this.clearActiveCheck();
        // Fresh socket so a prior probe's CONNECTING/retry loop cannot block recovery
        this.wsMap.get(serverUrl)?.disconnect();
        this.wsMap.set(serverUrl, new WsService());
        const wsService = this.wsMap.get(serverUrl);

        let settled = false;
        const publish = (state: Suspense<any>) => {
            if (settled) {
                return;
            }
            settled = true;
            this.clearConnectTimer();
            checkingState.next(state);
        };

        checkingState.next(SUSPENSE_INITIAL_STATE);
        wsService.setConfig(
            `${serverUrl}/ws/anonymous/`,
            undefined,
            {
                deserializer: msg => msg as any,
            },
            this.reconnectInterval,
            retryAttemps,
        );

        this.activeCheckSub = wsService.connect().subscribe({
            next: () =>
                publish({
                    ...SUSPENSE_INITIAL_STATE,
                    succeeded: true,
                    finished: true,
                    inProgress: false,
                }),
            error: error =>
                publish({
                    ...SUSPENSE_INITIAL_STATE,
                    succeeded: false,
                    finished: true,
                    inProgress: false,
                    error: {
                        errorCode: BaseErrors.WEBSOCKET,
                        originalError: error,
                    },
                }),
        });

        // Force-close sockets stuck in CONNECTING (Case C: DROP/blackhole, navigator.onLine true)
        this.connectTimer = setTimeout(() => {
            if (!settled && !wsService.connection) {
                try {
                    wsService.disconnect();
                } catch {
                    // Subject may already be closed in tests
                }
                publish({
                    ...SUSPENSE_INITIAL_STATE,
                    succeeded: false,
                    finished: true,
                    inProgress: false,
                    error: {
                        errorCode: BaseErrors.WEBSOCKET,
                        originalError: { message: 'WebSocket connect timeout' },
                    },
                });
            }
        }, this.connectTimeoutMs);

        return new Observable<Suspense<any>>(observer => {
            const stateSub = checkingState.subscribe(observer);
            return () => {
                stateSub.unsubscribe();
                // Abort: suppress failure from disconnect so mid-probe online (Case A) stays clean
                settled = true;
                this.clearActiveCheck();
                try {
                    wsService.disconnect();
                } catch {
                    // Subject may already be closed in tests
                }
            };
        });
    }

    /**
     * Resets checking state
     */
    reset(): void {
        this.clearActiveCheck();
        this.wsMap.forEach(ws => {
            try {
                ws.disconnect();
            } catch {
                // Subject may already be closed
            }
        });
        this.apiStateMap.forEach(s => s.next(SUSPENSE_INITIAL_STATE));
    }

    /**
     * Set wsMap and apiStateMap values if not exist
     * @param serverUrl
     */
    private setUrlMapsValues(serverUrl: string): void {
        this.wsMap.set(serverUrl, new WsService());
        this.apiStateMap.set(serverUrl, new BehaviorSubject(SUSPENSE_INITIAL_STATE));
    }

    private clearConnectTimer(): void {
        if (this.connectTimer != null) {
            clearTimeout(this.connectTimer);
            this.connectTimer = null;
        }
    }

    private clearActiveCheck(): void {
        this.clearConnectTimer();
        this.activeCheckSub?.unsubscribe();
        this.activeCheckSub = null;
    }
}
